/**
 * API Client for Admin Frontend
 * Wrapper nad /api/admin/* endpoints
 */

import axios from 'axios'

// VITE_API_BASE_URL should be the Worker API domain (e.g., https://api.genogram.wellysa.com)
// Full API paths are in the functions below (e.g., /api/admin/clinics)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.genogram.wellysa.com'

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add Authorization header to all requests if token exists
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * Authentication
 */
export const authAPI = {
  /**
   * Login
   * POST /api/admin/login
   * Body: { username, password } or { email, password }
   */
  login: async (username, password, email = null) => {
    const response = await apiClient.post('/api/admin/login', {
      username: username || null,
      email: email || null,
      password,
    })
    // Store token if returned
    if (response.data.token) {
      localStorage.setItem('admin_auth_token', response.data.token)
      if (response.data.user) {
        localStorage.setItem('admin_user', JSON.stringify(response.data.user))
      }
    }
    return response
  },
  
  /**
   * Logout
   * POST /api/admin/logout
   */
  logout: async () => {
    localStorage.removeItem('admin_auth_token')
    const response = await apiClient.post('/api/admin/logout')
    return response
  },
  
  /**
   * Check if user is authenticated
   * GET /api/admin/me
   */
  checkAuth: async () => {
    try {
      const token = localStorage.getItem('admin_auth_token')
      if (!token) return false
      
      const response = await apiClient.get('/api/admin/me')
      return response.status === 200
    } catch {
      return false
    }
  },
}

/**
 * Clinics API
 */
export const clinicsAPI = {
  /**
   * List all clinics
   * GET /api/admin/clinics
   */
  listClinics: async () => {
    const response = await apiClient.get('/api/admin/clinics')
    return response.data
  },
  
  /**
   * Get clinic by ID
   * GET /api/admin/clinics/{id}
   */
  getClinic: async (id) => {
    const response = await apiClient.get(`/api/admin/clinics/${id}`)
    return response.data
  },
  
  /**
   * Create clinic
   * POST /api/admin/clinics
   */
  createClinic: async (data) => {
    const response = await apiClient.post('/api/admin/clinics', data)
    return response.data
  },
  
  /**
   * Update clinic
   * PATCH /api/admin/clinics/{id}
   */
  updateClinic: async (id, data) => {
    const response = await apiClient.patch(`/api/admin/clinics/${id}`, data)
    return response.data
  },
  
  /**
   * Delete clinic (soft delete - set is_active = 0)
   * DELETE /api/admin/clinics/{id}
   */
  deleteClinic: async (id) => {
    const response = await apiClient.delete(`/api/admin/clinics/${id}`)
    return response.data
  },
}

export default apiClient
